import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { get } from 'lodash';
import { CodeResponse, useGoogleLogin } from '@react-oauth/google';

import { iconConstants, routesConstants } from '@/constants';
import axios from '@/axios';
import Icons from '@/icons';
import { authHelpers, generalHelpers } from '@/helpers';
import { Button, Input } from '@/kaya';
import { songsStore, useArtistStore, withdrawStore } from '@/store';
import { songActions, userActions } from '@/actions';
import GoogleButton from '@/component/googleButton/GoogleButton';

import styles from './Login.module.scss';
import logo from '@/assets/upperLogin-image.png';

const Login = () => {
  const { setArtist, setAuthentication, defaultArtist } = useArtistStore();
  const { setSongs } = songsStore();
  const { setWithdraw } = withdrawStore();

  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [err, setErr] = useState<{ [key: string]: string }>({
    username: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [googlLogin, setGoogleLogin] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();

  const handleEyeClick = () => {
    setShowPassword(!showPassword);
  };

  const handleLogin = async () => {
    const error = {
      username: authHelpers.validateUsernameOrEmail(username),
      password: generalHelpers.validatePassword(password),
    };

    if (!generalHelpers.isEmptyErrors(error)) {
      setErr(error);
      return;
    }
    // write api call here
    setLoading(true);

    const data = await userActions.loginAction({ payload: { email: username, password } });
    if (!data.success) return setLoading(false);

    setArtist({
      ...defaultArtist,
      id: get(data, 'data.id', ''),
    });

    localStorage.setItem('refresh_token', get(data, 'data.refresh_token', ''));

    axios.defaults.headers.common['Authorization'] = `Bearer ${get(data, 'data.access_token', '')}`;

    const artistData = await userActions.fetchArtistAction(get(data, 'data.id', ''));
    if (artistData.data.code === 'PHONE_NOT_VERIFIED') {
      setAuthentication({
        isAuthenticated: true,
        isPhoneVerified: false,
      });
      navigate(routesConstants.ROUTES.PERSONAL_INFO);
    } else {
      const songsData = await songActions.fetchSongsAction(get(artistData, 'data.data.id', {}));

      if (songsData.success) {
        setSongs(get(songsData, 'data', []));
      }
      const WithdrawalData = await userActions.fetchWithdrawalAction(
        get(artistData, 'data.data.id', {}),
      );
      if (WithdrawalData.success) {
        setWithdraw(get(WithdrawalData, 'data', {}));
      }

      setAuthentication({
        isAuthenticated: true,
        isPhoneVerified: true,
      });

      setArtist(get(artistData, 'data.data', {}));
      navigate(routesConstants.ROUTES.HOME);
    }

    setLoading(false);
  };

  const handleSignUp = () => {
    navigate(routesConstants.ROUTES.REGISTER);
  };

  const handleForgotPassword = () => {
    navigate(routesConstants.ROUTES.FORGOT_PASSWORD);
  };

  const onSuccess = async (res: CodeResponse) => {
    try {
      setGoogleLogin(true);
      const { code } = res;

      const data = await userActions.googleLoginAction(code);

      if (!data.success) return;

      setArtist({
        ...defaultArtist,
        id: get(data, 'data.id', {}),
      });

      localStorage.setItem('refresh_token', get(data, 'data.refresh_token', ''));

      axios.defaults.headers.common['Authorization'] = `Bearer ${get(
        data,
        'data.access_token',
        '',
      )}`;

      const artistData = await userActions.fetchArtistAction(get(data, 'data.id', ''));

      if (artistData.data.code === 'PHONE_NOT_VERIFIED') {
        setAuthentication({
          isAuthenticated: true,
          isPhoneVerified: false,
        });
        navigate(routesConstants.ROUTES.PERSONAL_INFO);
        return;
      } else {
        const songsData = await songActions.fetchSongsAction(get(data, 'data.id', {}));

        if (songsData.success) {
          setSongs(get(songsData, 'data', []));
        }
        setAuthentication({
          isAuthenticated: true,
          isPhoneVerified: true,
        });

        setArtist(get(artistData, 'data.data', {}));
        navigate(routesConstants.ROUTES.HOME);
      }

      // const userToUpdate = {
      //   ...userData,
      //   calendarEmail,
      // };
      // updateUser(userToUpdate);
    } catch (error: any) {
      generalHelpers.toaster('error', error.message || 'Something went wrong');
    } finally {
      setGoogleLogin(false);
    }
  };

  const onError = (err: Pick<CodeResponse, 'error_description'>) => {
    const { error_description } = err;
    generalHelpers.toaster('error', error_description || 'Something went wrong');
  };

  const googleLogin = useGoogleLogin({
    flow: 'auth-code',
    onSuccess,
    onError,
    //scope: only
  });

  return (
    <div className={styles.Login}>
      <div className={styles.logo}>
        {/*         <img src={logo} alt="logo" className={styles.logoImg} />
         */}
        <img src={logo} alt="logo" className={styles.logoImg} />
      </div>
      <div className={styles.boxLogin}>
        <h1 className={styles.title}>Login</h1>
        <p className={styles.title2}>Welcome to For Play artist zone</p>

        <div className={styles.box}>
          <Input
            type="string"
            placeholder="Username or Email"
            containerClassName={styles.input1}
            onChange={(e) => {
              setUsername(e);
              setErr({
                ...err,
                ['username']: '',
              });
            }}
            errors={err}
            id="username"
          />
          <Input
            type={showPassword ? 'string' : 'password'}
            placeholder="Password"
            containerClassName={styles.input2}
            onChange={(e) => {
              setPassword(e);
              setErr({
                ...err,

                ['password']: '',
              });
            }}
            errors={err}
            id="password"
            onEnterClick={handleLogin}
            suffixIcon={
              <Icons type={showPassword ? iconConstants.ICONS.EYEOFF : iconConstants.ICONS.EYEON} />
            }
            onSuffixIconClick={handleEyeClick}
          />
          <p className={styles.text} onClick={handleForgotPassword}>
            Forgot Password?
          </p>

          <Button className={styles.btn1} onClick={handleLogin} isLoading={loading} type="Primary">
            Login
          </Button>
          <GoogleButton className={styles.btn2} onClick={googleLogin} isLoading={googlLogin} />
          <div className={styles.account}>
            <div className={styles.line1}></div>
            <p className={styles.text2}>Don’t have an account?</p>
            <div className={styles.line2}></div>
          </div>
          <Button className={styles.btn3} type="Outline" onClick={handleSignUp}>
            Sign Up
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Login;
